// 70/30 Hybrid Intelligence - Lokale Intent-Detection + Mistral LLM
import { queryClaraLLM } from './ClaraBridgeLLM.js';
import { masterSupabase } from "../lib/supabase-master.js";

class HybridIntelligence {
  constructor() {
    this.confidence = 0;
    this.lastIntent = null;
  }

  // 70% LOKALE INTELLIGENZ - Intent Detection
  detectIntent(message) {
    const lowerMessage = message.toLowerCase();
    
    // Immobilien-Intents mit Patterns
    const intents = {
      mieteinnahmen: {
        patterns: ['mieteinnahmen', 'miete', 'einnahmen', 'monatlich', 'mieterlös'],
        confidence: 0.9,
        response: 'Für Informationen zu Mieteinnahmen benötige ich Zugriff auf Ihre Immobiliendatenbank. Derzeit sind keine Daten verfügbar.'
      },
      rückstände: {
        patterns: ['rückstände', 'rückstand', 'ausstehend', 'offene', 'zahlungsrückstand'],
        confidence: 0.9,
        response: 'Derzeit sind keine Mietrückstände in der Datenbank verzeichnet.'
      },
      wartung: {
        patterns: ['wartung', 'reparatur', 'instandhaltung', 'defekt', 'kaputt'],
        confidence: 0.8,
        response: 'Für Wartungsanfragen benötige ich Zugriff auf das Wartungssystem. Derzeit sind keine Daten verfügbar.'
      },
      vermietung: {
        patterns: ['vermietung', 'leerstand', 'frei', 'vermieten', 'auslastung'],
        confidence: 0.8,
        response: 'Informationen zur Vermietungssituation sind derzeit nicht verfügbar.'
      },
      kosten: {
        patterns: ['kosten', 'ausgaben', 'betriebskosten', 'nebenkosten'],
        confidence: 0.8,
        response: 'Für Kosteninformationen benötige ich Zugriff auf die Finanzdatenbank.'
      },
      objekte: {
        patterns: ['objekt', 'immobilie', 'wohnung', 'haus', 'gebäude'],
        confidence: 0.7,
        response: 'Für Objektinformationen benötige ich Zugriff auf die Immobiliendatenbank.'
      },
      greeting: {
        patterns: ['hey clara', 'hallo clara', 'hi clara', 'clara', 'hey', 'hallo', 'hi'],
        confidence: 0.95,
        response: 'Hallo! Ich bin Clara, Ihre KI-Assistentin für Immobilienverwaltung. Wie kann ich Ihnen heute helfen?'
      },
      voice: {
        patterns: ['voice', 'sprache', 'mikrofon', 'sprechen', 'hören'],
        confidence: 0.9,
        response: 'Die Sprachfunktion ist aktiv. Sie können mit mir sprechen, indem Sie das Mikrofon-Symbol drücken.'
      }
    };

    // Finde besten Intent-Match
    let bestMatch = null;
    let bestScore = 0;

    for (const [intent, config] of Object.entries(intents)) {
      for (const pattern of config.patterns) {
        if (lowerMessage.includes(pattern)) {
          const score = config.confidence * (pattern.length / lowerMessage.length);
          if (score > bestScore) {
            bestScore = score;
            bestMatch = { intent, config, score };
          }
        }
      }
    }

    return bestMatch;
  }

  // 30% MISTRAL LLM - Für komplexe Fragen
  async queryMistral(message, context = '') {
    try {
      console.log('🧠 Querying Mistral for complex question:', message);
      const response = await queryClaraLLM(message, context);
      return {
        success: true,
        response: response.content || response,
        source: 'mistral'
      };
    } catch (error) {
      console.error('❌ Mistral query failed:', error);
      return {
        success: false,
        response: 'Entschuldigung, ich kann diese Frage momentan nicht beantworten. Das KI-System ist nicht verfügbar.',
        source: 'fallback'
      };
    }
  }

  // HAUPTFUNKTION - Hybrid-Verarbeitung
  async processMessage(message) {
    console.log('🔄 Processing message with Hybrid Intelligence:', message);

    // 1. LOKALE INTENT-DETECTION (70%)
    const intentMatch = this.detectIntent(message);
    
    if (intentMatch && intentMatch.score > 0.6) {
      console.log('✅ Local intent detected:', intentMatch.intent, 'confidence:', intentMatch.score);
      this.confidence = intentMatch.score;
      this.lastIntent = intentMatch.intent;
      
      return {
        response: intentMatch.config.response,
        confidence: this.confidence,
        source: 'local',
        intent: intentMatch.intent
      };
    }

    // 2. MISTRAL LLM FALLBACK (30%)
    console.log('🤖 No local intent match, querying Mistral...');
    const mistralResult = await this.queryMistral(message);
    
    this.confidence = mistralResult.success ? 0.7 : 0.3;
    this.lastIntent = 'complex_query';

    return {
      response: mistralResult.response,
      confidence: this.confidence,
      source: mistralResult.source,
      intent: 'complex_query'
    };
  }

  // COMPATIBILITY METHOD - Wrapper für processMessage
  async processUserInput(userInput, context = {}) {
    console.log("🔄 processUserInput called with:", userInput, context);
    
    // Store context for potential use
    this.context = context;
    
    // Call existing processMessage method
    const result = await this.processMessage(userInput);
    
    // Ensure result has expected format
    return {
      response: result.response || "Entschuldigung, ich konnte das nicht verarbeiten.",
      action: result.action || null,
      confidence: result.confidence || 0.5,
      source: result.source || "hybrid",
      intent: result.intent || "unknown"
    };
  }

  // Hilfsfunktionen
  getLastConfidence() {
    return this.confidence;
  }

  getLastIntent() {
    return this.lastIntent;
  }
}

// Export singleton instance
export default new HybridIntelligence();
